"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Newspaper,
  Sparkles,
  Stethoscope,
  Crown,
  Brain,
  Mic,
  ArrowRight,
  Heart,
  Lock,
  Compass,
  Activity,
  Briefcase,
  Building2,
  Target,
  Users,
  Globe2,
  Clock,
  MessageSquare,
  PlayCircle,
  Headphones,
  Video,
  ShieldCheck,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const TOPICS = [
  {
    title: "Operational Excellence",
    body: "The mechanics of execution, the architecture of accountability, the discipline that produces consistent, measurable outcomes at sovereign scale. The perspective of someone who has built and sustained operational excellence — why execution fails where strategy succeeds, why accountability structures outperform motivation campaigns, why operational discipline is the differentiator.",
    accent: "red",
  },
  {
    title: "Women in Leadership and the C-Suite",
    body: "The 70% evidence, the operational case, the decade of data proving that teams composed of over seventy percent women produce the most reliable outcomes. Not motivational. Operational — grounded in verified results from assembling majority-female teams across eighteen countries and measuring performance against conventional staffing models.",
    accent: "emerald",
  },
  {
    title: "Governance Transformation",
    body: "The technology infrastructure, policy frameworks, and citizen-centric design that has served over nine hundred million citizens across eighteen countries. The perspective of someone who has built and run governance systems at national scale — the operational reality of building systems that serve populations.",
    accent: "purple",
  },
  {
    title: "Crisis Management & Organizational Resilience",
    body: "The clinical method for crisis response, the protocol-driven approach to organizational survival, the discipline that has produced zero security incidents across fifteen-plus years. The perspective of someone who has navigated crises where the stakes were national — protocol-driven response, triage under pressure, recovery with operational continuity.",
    accent: "cyan",
  },
  {
    title: "AI Governance & Technology Ethics",
    body: "The operational reality of deploying artificial intelligence at sovereign scale, the ethical frameworks that govern technology deployment, the intersection of human judgment and machine intelligence. The perspective of someone who has built and run proprietary AI/ML platforms at sovereign scale for over a decade.",
    accent: "denim",
  },
  {
    title: "Healthcare Leadership & Physician Perspective",
    body: "The clinical method as an operating system, the diagnostic discipline applied to organizational challenges, the unique perspective an MBBS-qualified physician brings to business leadership. The perspective of someone who transitioned from medical practice to sovereign-scale operations — the clinical method demonstrated as an operating system, not a metaphor.",
    accent: "amber-gold",
  },
];

const FORMATS = [
  {
    icon: Video,
    title: "On-Record Interviews",
    body: "Television, print, digital, and broadcast interviews where the commentary is precise, evidence-based, and grounded in verified operational data. Every statement is defensible. Every claim is traceable to verified outcomes. Prepared with the same diagnostic rigor that governs sovereign-level engagements.",
    accent: "red",
  },
  {
    icon: Users,
    title: "Expert Panels & Roundtables",
    body: "Multi-participant discussions where cross-domain perspective enriches coverage. Short, confident, factual, and grounded in verified experience. The Triple Threat — medicine, technology, and politics as one operating method — brings a dimension to panel discussions single-domain experts cannot replicate.",
    accent: "purple",
  },
  {
    icon: Lock,
    title: "Background Briefings",
    body: "Off-record briefings for journalists conducting investigations or preparing in-depth coverage. The most discreet format — journalists receive operational perspective without attribution, enabling deeper and more nuanced coverage. Conducted under the same confidentiality standards that govern sovereign-level engagements.",
    accent: "cyan",
  },
  {
    icon: MessageSquare,
    title: "Quoted Commentary",
    body: "Written statements for direct quotation in articles, features, and news coverage. Maximum precision — every word selected for impact, accuracy, and defensibility. Grounded in the same verified operational data that drives sovereign-scale engagements. Concise, authoritative, designed to add operational depth.",
    accent: "emerald",
  },
  {
    icon: Headphones,
    title: "Podcast Appearances",
    body: "Long-form conversations exploring operational topics in depth. The format allows for nuanced, detailed exploration that shorter formats cannot accommodate. The operating method explained at length, the clinical parallels drawn explicitly, the verified outcomes documented in detail.",
    accent: "amber-gold",
  },
  {
    icon: PlayCircle,
    title: "Video & Documentary",
    body: "Visual commentary for news segments, documentary features, and video publications. Authoritative presence, clear articulation, the ability to convey complex operational realities in accessible form. Prepared with the same diagnostic rigor as every other format.",
    accent: "denim",
  },
];

const STANDARDS = [
  { num: "01", title: "Response Time", body: "Inquiries acknowledged within four hours during business hours. Preliminary responses within twenty-four hours. For time-sensitive coverage, expedited protocols activate with same-day commentary. Operational commitment, not courtesy.", accent: "red" },
  { num: "02", title: "Preparation", body: "Every commentary engagement begins with a diagnostic phase — understanding the publication, the journalist&apos;s inquiry, the editorial context, the angle. Completed within 24–48 hours, after which commentary is prepared with precision and evidence.", accent: "purple" },
  { num: "03", title: "Accuracy", body: "Every statement grounded in verified operational data. Claims traceable to documented outcomes. Metrics drawn from the same source of truth. No speculation, no extrapolation, no perspectives that cannot be substantiated.", accent: "cyan" },
  { num: "04", title: "Discretion", body: "National security level confidentiality governs every media interaction. Background briefings off the record unless explicitly agreed. Client names, country specifics, and operational details never disclosed.", accent: "emerald" },
  { num: "05", title: "Follow-Up", body: "Commentary engagements include follow-up availability for clarification, additional context, and expanded coverage. Responses within twenty-four hours — the same standards as initial engagement.", accent: "amber-gold" },
  { num: "06", title: "Professionalism", body: "Arrives prepared, delivers with precision, follows through on commitments. The media commentary service is a dedicated engagement type, supported by the same operational infrastructure that serves sovereign-level clients.", accent: "denim" },
];

const MEDIA = [
  { icon: Briefcase, title: "Business Publications", body: "Outlets covering corporate leadership, operational strategy, and organizational transformation — publications where the audience is executives, board members, and senior leaders.", accent: "red" },
  { icon: Activity, title: "Technology Outlets", body: "Publications covering AI, digital transformation, cybersecurity, and technology governance — where the audience is technology leaders, CIOs, CTOs, and technology professionals.", accent: "purple" },
  { icon: Stethoscope, title: "Healthcare Media", body: "Outlets covering healthcare leadership, medical technology, health policy — bridging clinical practice and operational leadership through a physician&apos;s perspective.", accent: "cyan" },
  { icon: Building2, title: "Governance & Policy Journals", body: "Publications covering public administration, governance reform, digital governance — where the audience is policymakers, government officials, and governance professionals.", accent: "emerald" },
  { icon: Heart, title: "Women&apos;s Leadership Platforms", body: "Outlets focused on women in leadership, diversity in the workplace, and female empowerment — providing evidence-based perspective, not motivational platitudes.", accent: "amber-gold" },
  { icon: Globe2, title: "International Media", body: "Outlets covering global operations, geopolitics, and international strategy — where the audience demands cross-domain perspective grounded in sovereign-scale experience.", accent: "denim" },
];

export default function PressMediaCommentaryPage() {
  return (
    <>
      <PageHero
        icon={Newspaper}
        overline="Service · Press & Media Commentary"
        overlineClassName="text-denim"
        title={
          <>
            Expert Perspective, <span className="text-gradient-premium">Operational Intelligence.</span>
          </>
        }
        subtitle="Press and media commentary that is not opinion — it is operational intelligence delivered in accessible form. For journalists, editors, producers, and media organizations seeking authoritative perspective grounded in verified sovereign-scale experience."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Press & Media Commentary" }]}
        badge="Substance over soundbites"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
              <Sparkles className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Most commentators theorize. This commentary operationalizes.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every perspective is extracted from verified operational experience — the kind that has been tested across eighteen countries and fifteen-plus years of continuous operation. The frameworks are not theoretical models. They are operating mechanisms that have produced verified outcomes at sovereign scale.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The medical background adds a dimension no other commentator at this level brings. The discipline of medicine — diagnosis before treatment, triage under pressure, outcome accountability — is not a metaphor. It is the actual operating system being described. The delivery room and the boardroom share the same discipline.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The preparation behind every commentary mirrors the preparation behind every sovereign-level engagement. Before engaging with any media organization, the same diagnostic phase is applied: understanding the publication, its audience, the editorial context, and the specific angle. The commentary is then calibrated — not genericized — to address the specific realities of the story.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { title: "Evidence Over Assertion", body: "Every statement grounded in verified operational data. Not synthesized from books or consulting engagements. Extracted from sovereign-scale experience where the margin for error is zero.", accent: "red" },
              { title: "Substance Over Soundbites", body: "The operational depth that transforms coverage from reporting to insight. The discipline of medicine, the architecture of technology, the strategic discipline of politics — all available to the story.", accent: "purple" },
              { title: "Cross-Domain Authority", body: "The Triple Threat — medicine, technology, and politics as one operating method — enriches coverage across beats. A perspective that considers operational, technological, political, and human dimensions at once.", accent: "cyan" },
              { title: "Discretion as Discipline", body: "National security level confidentiality. Background briefings stay in briefings. What is confirmed on record is grounded in verified operational data, not speculation.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                >
                  <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                  <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Topics Available for Commentary</span>
          </div>
          <h2 className="text-display-md mb-4">Six core topic areas, each grounded in operational reality.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The commentary spans the full spectrum of operational expertise — each topic offering the kind of perspective that transforms coverage from reporting into insight.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOPICS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg">
                    <Target className="size-5 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{t.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Commentary Formats</span>
          </div>
          <h2 className="text-display-md mb-4">Flexibility for every media requirement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The commentary is available in multiple formats — each adapted to the journalist&apos;s needs, the publication&apos;s requirements, and the story&apos;s stakes. Every format maintains the same operational standards: informed perspective grounded in verified experience, timely delivery, professional conduct, and absolute discretion.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATS.map((f, i) => {
            const a = ACCENT[f.accent];
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg`}>
                  <f.icon className="size-5 text-white" />
                </div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{f.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Clock className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Response Standards</span>
          </div>
          <h2 className="text-display-md mb-4">Timely, professional, discreet.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every media engagement is governed by response standards that match the demands of modern news cycles while maintaining the confidentiality that sovereign-level work requires. Operational protocols, enforced with the same discipline that governs sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STANDARDS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Newspaper className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Ideal Media</span>
          </div>
          <h2 className="text-display-md mb-4">Publications, outlets, and platforms.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The commentary is designed for media organizations that serve audiences demanding operational substance. The following categories represent the ideal media landscape for commentary engagements.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MEDIA.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <m.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{m.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Newspaper className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Media Categories</span>
          </div>
          <h2 className="text-display-md mb-4">Five commentary categories, calibrated to the story.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Press commentary is organized into five categories, each with its own preparation protocol, evidentiary base, and delivery standard. The category is matched to the story and the publication.
          </p>
        </div>
        <TabsBlock
          defaultValue="strategic"
          tabs={[
            {
              label: "Strategic Intelligence",
              value: "strategic",
              icon: <Brain className="size-3.5" />,
              content: (
                <div className="space-y-4 text-sm leading-relaxed text-platinum-300/90">
                  <p className="text-base font-semibold text-platinum-50">Commentary for stories that demand cross-domain perspective.</p>
                  <p>The strategic intelligence category addresses stories that span operational, technological, political, and human dimensions simultaneously. The commentary is grounded in the Triple Threat — medicine, technology, and politics as one operating method.</p>
                  <p>Preparation includes a 24-hour intelligence review drawing from data across hundreds of social platforms, news sources, and languages. The commentary is calibrated to the publication's editorial position and the story's specific angle.</p>
                  <p>Delivery standards: precision over rhetoric, evidence over assertion, operational depth over theoretical depth. The journalist receives commentary that transforms coverage from reporting to insight.</p>
                </div>
              ),
            },
            {
              label: "Operational Excellence",
              value: "operational",
              icon: <Target className="size-3.5" />,
              content: (
                <div className="space-y-4 text-sm leading-relaxed text-platinum-300/90">
                  <p className="text-base font-semibold text-platinum-50">Commentary for stories about execution, accountability, and operational performance.</p>
                  <p>The operational excellence category covers stories about organizational performance, execution gaps, accountability failures, and the discipline that produces consistent results under pressure.</p>
                  <p>Preparation includes review of the specific organizational context, the operational challenges in evidence, and the verifiable benchmarks that contextualize the commentary.</p>
                  <p>Delivery standards: protocols over opinions, evidence over intuition, mechanisms over motivations. The journalist receives commentary that adds operational depth to the story.</p>
                </div>
              ),
            },
            {
              label: "Women in Leadership",
              value: "women",
              icon: <Heart className="size-3.5" />,
              content: (
                <div className="space-y-4 text-sm leading-relaxed text-platinum-300/90">
                  <p className="text-base font-semibold text-platinum-50">Commentary grounded in the 70% evidence base.</p>
                  <p>The women in leadership category draws from a decade of operational data proving that teams composed of over 70% women produce the most reliable outcomes. The commentary is operational, not motivational — evidence, not ideology.</p>
                  <p>Preparation includes review of the specific story context, the operational evidence that applies, and the verifiable outcomes that have been measured across sovereign-scale deployments.</p>
                  <p>Delivery standards: the operational case, not the moral case. The journalist receives commentary that transforms diversity from aspiration into operational advantage.</p>
                </div>
              ),
            },
            {
              label: "Crisis & Resilience",
              value: "crisis",
              icon: <ShieldCheck className="size-3.5" />,
              content: (
                <div className="space-y-4 text-sm leading-relaxed text-platinum-300/90">
                  <p className="text-base font-semibold text-platinum-50">Commentary for stories about organizational crisis, resilience, and recovery.</p>
                  <p>The crisis and resilience category covers stories about organizational failure, recovery, and the discipline that produces survival under sovereign-level pressure. The clinical method is the framework.</p>
                  <p>Preparation includes review of the specific crisis context, the operational parallels to the story, and the protocols that have sustained zero security incidents across sovereign-scale operations.</p>
                  <p>Delivery standards: triage, protocol, judgment, outcome accountability. The journalist receives commentary that explains what survival under sovereign pressure actually requires.</p>
                </div>
              ),
            },
            {
              label: "AI & Technology",
              value: "tech",
              icon: <Headphones className="size-3.5" />,
              content: (
                <div className="space-y-4 text-sm leading-relaxed text-platinum-300/90">
                  <p className="text-base font-semibold text-platinum-50">Commentary for stories about AI governance, technology ethics, and cross-domain technology leadership.</p>
                  <p>The AI and technology category draws from a decade of building and running proprietary AI/ML platforms at sovereign scale. The perspective is operational, not speculative — what the actual deployment of AI at scale requires.</p>
                  <p>Preparation includes review of the technology story, the governance frameworks that apply, and the ethical structures that have governed technology deployment in sovereign-level operations.</p>
                  <p>Delivery standards: operational reality over speculative framing. The journalist receives commentary that explains AI governance from someone who has built and run the systems.</p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Clock className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Engagement Cadence</span>
          </div>
          <h2 className="text-display-md mb-4">Press engagement workflow and timelines.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Press commentary operates on disciplined timelines that match modern news cycles while maintaining the standards sovereign-level work requires. The cadence is transparent, the response is consistent.
          </p>
        </div>
        <CardGridBlock
          variant="number"
          columns={4}
          items={[
            { title: "Inquiry Acknowledgment", body: "All inquiries acknowledged within four hours during business hours. Acknowledgment includes preliminary assessment of the request and the engagement model that will apply.", accent: "red" },
            { title: "Diagnostic Call", body: "Preliminary diagnostic conversation within 24 hours. The conversation maps the publication, the editorial context, the journalist's specific inquiry, and the timeline requirements.", accent: "purple" },
            { title: "Commentary Preparation", body: "Commentary prepared within 24 to 48 hours. Preparation includes intelligence review, evidence assembly, drafting, verification, and final review. Expedited timelines accommodated for time-sensitive stories.", accent: "cyan" },
            { title: "Delivery and Follow-Up", body: "Commentary delivered with on-call availability for follow-up. Follow-up support continues through publication and post-publication cycles. Clarifications and additional context provided within 24 hours.", accent: "emerald" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions journalists and editors ask before engaging.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Press commentary is operationally disciplined. The questions below address what journalists and editors most frequently ask before initiating an engagement.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "How quickly can commentary be provided for breaking news?", a: "For time-sensitive stories, expedited protocols activate with same-day commentary. The preparation phase compresses from 24 to 48 hours to a same-day turnaround. The diagnostic conversation is conducted within hours. Expedited timelines are evaluated individually based on the story's stakes and the commentary's requirements." },
            { q: "Is on-the-record commentary available?", a: "Yes. On-record commentary is provided in all formats — television, print, digital, broadcast, podcast, and documentary. On-record statements are precise, evidence-based, and grounded in verified operational data. Every claim is traceable to documented outcomes." },
            { q: "How are background briefings handled?", a: "Background briefings are conducted under the most stringent confidentiality standards. Journalists receive operational perspective without attribution, enabling deeper and more nuanced coverage. The briefings are off the record unless explicitly agreed otherwise. Client names, country specifics, and operational details are never disclosed." },
            { q: "What subjects are off-limits for commentary?", a: "Active client matters, ongoing sovereign-level engagements, and confidential operational details are off-limits. The commentary draws from sovereign-scale experience but never from specific client work. The discretion standard is absolute — client confidentiality governs every engagement." },
            { q: "How is the editorial independence of the publication protected?", a: "Commentary is provided as input to the journalist's work, not as approved or pre-cleared content. The publication retains complete editorial independence. The commentary adds operational depth to the story; the publication decides how to use it. No editorial approval is sought or provided." },
            { q: "Can the commentary be used across multiple platforms?", a: "Yes. Commentary provided for a specific story can be used across the publication's platforms — print, digital, broadcast, social — as the publication's editorial workflow requires. Multi-platform use is accommodated within the original engagement scope. Cross-publication syndication is handled separately." },
            { q: "What languages are supported?", a: "Primary commentary is provided in English. Interpreters can be arranged for specific engagements requiring other languages. Multi-language coverage is available for international publications and global media organizations. The diagnostic conversation addresses language requirements upfront." },
            { q: "How is the relationship with the press managed over time?", a: "Standing relationships with publications and journalists are developed through consistent quality, reliable turnaround, and the operational depth that transforms coverage. The relationship is built on substance, not access. Repeat engagement is earned through verified contribution to the publication's editorial quality." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Newspaper className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about media commentary.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every commentary engagement begins with a diagnostic phase — understanding the publication, the journalist&apos;s inquiry, the editorial context. The commentary is then calibrated to address the specific realities of the story.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Inquire About Commentary
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/writing-thought-leadership/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Brain className="size-4" />
                Writing & Thought Leadership
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Writing & Thought Leadership", description: "Distributed advisory through The Operator&apos;s Notebook and the 70% Project.", href: "/services/writing-thought-leadership/", icon: Brain, accent: "amber-gold" },
          { title: "Keynote Speaking", description: "Operational authority delivered from the stage. Six signature topics.", href: "/services/keynote-speaking/", icon: Mic, accent: "red" },
          { title: "University Lectures", description: "Academic lectures for business schools and executive education programs.", href: "/services/university-lectures/", icon: Crown, accent: "coral" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "denim" },
        ]}
      />
    </>
  );
}
